/**
 * SVGUtils — Shared utility functions for procedural SVG generation.
 *
 * Eliminates duplicated SVG patterns across mob renderers, item icons,
 * and texture generators. All functions are pure and stateless.
 */

/** Encode raw SVG string to a base64 data URL for Phaser texture loading. */
export function svgToBase64(svg: string): string {
  return 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svg)));
}

/** Decode a base64 data URL back to raw SVG for inline React rendering. */
export function svgFromBase64(dataUrl: string): string {
  const base64 = dataUrl.replace('data:image/svg+xml;base64,', '');
  return decodeURIComponent(escape(atob(base64)));
}

/** Wrap SVG content in a root <svg> element with xmlns and dimensions. */
export function svgWrap(width: number, height: number, content: string, viewBox?: string): string {
  const vb = viewBox ?? `0 0 ${width} ${height}`;
  return `<svg width="${width}" height="${height}" viewBox="${vb}" xmlns="http://www.w3.org/2000/svg">${content}</svg>`;
}

/** Render a standard drop shadow ellipse (used by most mob sprites). */
export function svgShadow(cx: number, cy: number, rx: number, ry: number, opacity = 0.15): string {
  return `<ellipse cx="${cx}" cy="${cy}" rx="${rx}" ry="${ry}" fill="rgba(0,0,0,${opacity})"/>`;
}

/** Standard outline stroke attribute string. */
export function svgStroke(width = 1.5, opacity = 0.2): string {
  return `stroke="rgba(0,0,0,${opacity})" stroke-width="${width}"`;
}

/** Render a pair of standard eyes (white sclera + colored pupil + white highlight). */
export function svgEyes(config: {
  leftCx: number; leftCy: number;
  rightCx: number; rightCy: number;
  rx?: number; ry?: number;
  pupilR?: number; pupilColor?: string;
  highlightR?: number;
}): string {
  const {
    leftCx, leftCy, rightCx, rightCy,
    rx = 3.5, ry = 4,
    pupilR = 2, pupilColor = '#333',
    highlightR = 0.8,
  } = config;
  return [
    `<ellipse cx="${leftCx}" cy="${leftCy}" rx="${rx}" ry="${ry}" fill="white"/>`,
    `<ellipse cx="${rightCx}" cy="${rightCy}" rx="${rx}" ry="${ry}" fill="white"/>`,
    `<circle cx="${leftCx + 1}" cy="${leftCy + 1}" r="${pupilR}" fill="${pupilColor}"/>`,
    `<circle cx="${rightCx + 1}" cy="${rightCy + 1}" r="${pupilR}" fill="${pupilColor}"/>`,
    `<circle cx="${leftCx + 1.5}" cy="${leftCy - 1.5}" r="${highlightR}" fill="white"/>`,
    `<circle cx="${rightCx + 1.5}" cy="${rightCy - 1.5}" r="${highlightR}" fill="white"/>`,
  ].join('\n    ');
}

/** Render a highlight path with configurable opacity. */
export function svgHighlight(path: string, opacity = 0.2): string {
  return `<path d="${path}" fill="rgba(255,255,255,${opacity})" stroke="none"/>`;
}

/** Render a linear gradient definition for use in <defs>. */
export function svgGradientDef(id: string, color1: string, color2: string, x1 = '0%', y1 = '0%', x2 = '0%', y2 = '100%'): string {
  return `<linearGradient id="${id}" x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}">
    <stop offset="0%" stop-color="${color1}"/>
    <stop offset="100%" stop-color="${color2}"/>
  </linearGradient>`;
}

/** Render a radial gradient definition for use in <defs>. */
export function svgRadialGradientDef(id: string, innerColor: string, outerColor: string, cx = '50%', cy = '50%', r = '50%'): string {
  return `<radialGradient id="${id}" cx="${cx}" cy="${cy}" r="${r}">
    <stop offset="0%" stop-color="${innerColor}"/>
    <stop offset="100%" stop-color="${outerColor}"/>
  </radialGradient>`;
}

/** Simple curved mouth path. */
export function svgMouth(cx: number, cy: number, width = 6, curvature = 3): string {
  const left = cx - width / 2;
  const right = cx + width / 2;
  return `<path d="M${left},${cy} Q${cx},${cy + curvature} ${right},${cy}" stroke="#333" fill="none" stroke-width="1.2"/>`;
}

/** Darken a hex color by a percentage (0-100). Shared by mob renderers for
 *  tint-aware shading — one implementation instead of per-file copies. */
export function svgDarken(hex: string, percent: number): string {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = Math.max(0, (num >> 16) - Math.round(255 * percent / 100));
  const g = Math.max(0, ((num >> 8) & 0xff) - Math.round(255 * percent / 100));
  const b = Math.max(0, (num & 0xff) - Math.round(255 * percent / 100));
  return `#${(r << 16 | g << 8 | b).toString(16).padStart(6, '0')}`;
}
